/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/_chrome/play-signal.ts` (unchanged).
 *
 * No `next/*`, no server sink; `play.feedback` is already mounted on the Hono
 * host (`excellent/server/routes/pulse.ts`), so this works at runtime here today.
 */

import { notifyLive } from "@excellent/ui/api/live";
import { apiCall } from "@excellent/ui/api";
import { play, type PlayActionDTO, type PlaySignal } from "@excellent/ui/api/contracts/play";

/**
 * Post a Play feedback signal — the recursive self-improvement ledger write
 * shared by both Play surfaces (the home "What to do next" list and the Play
 * dialog driver). Owning it once keeps the feedback post + re-rank fan-out from
 * drifting between the two callers.
 *
 * Best-effort: each surface has already advanced its UI optimistically, so a
 * failed post is swallowed. Decision signals (anything but the silent 'shown'
 * impression) fan a `play` re-rank to every live surface and return `true` so a
 * caller can chain its own refresh (the home list re-fetches its inline rows;
 * the dialog keeps its frozen snapshot). Impressions stay silent and return
 * `false`.
 */
export async function postPlaySignal(
  action: PlayActionDTO,
  sig: PlaySignal,
  reason?: string,
): Promise<boolean> {
  try {
    await apiCall(play.feedback, {
      body: {
        actionKey: action.key,
        kind: action.kind,
        signal: sig,
        reason,
        // Carry the card's source event so a DISMISS override is chained to the
        // recommendation in the causal graph (§6.5.8).
        causationUid: action.causationUid ?? null,
      },
    });
  } catch {
    // best-effort; the surface already updated optimistically
  }
  if (sig !== "shown") {
    notifyLive("play");
    return true;
  }
  return false;
}
